// What an item is controlled FOR, shared by every place the classification is shown (chat card,
// progress panel, report card, saved ticket, PDF). MIRRORS the helpers of the same names in
// pyapi/app/workflow/export_design_workflow.py (_reason_codes, _control_reasons, _primary_control,
// _licence_posture): the backend writes the chat message, this writes everything else, and the two
// must say the same thing — change one, change the other.
//
// Why it exists: "EAR-listed" only says an item has an ECCN. What someone has to DO depends on the
// ECCN and its reasons for control (each read against the Country Chart per destination): an item
// controlled for National Security / Encryption and one controlled for Anti-Terrorism only are worlds
// apart. So the answer leads with the control and its reasons, and the regime is a label beside them.

export const CONTROL_REASON_NAMES: Record<string, string> = {
  NS: 'National Security',
  RS: 'Regional Stability',
  MT: 'Missile Technology',
  NP: 'Nuclear Nonproliferation',
  CB: 'Chemical & Biological Weapons',
  CW: 'Chemical Weapons Convention',
  CC: 'Crime Control',
  FC: 'Firearms Convention',
  EI: 'Encryption Items',
  SI: 'Significant Items',
  UN: 'United Nations',
  SL: 'Short Supply',
  AT: 'Anti-Terrorism',
};

// Display order: the substantive reasons first, Anti-Terrorism (the baseline on almost every entry) last.
const CONTROL_REASON_ORDER = ['NS', 'RS', 'MT', 'NP', 'CB', 'CW', 'CC', 'FC', 'EI', 'SI', 'UN', 'SL', 'AT'];
const REASON_CODE_RE = new RegExp(`\\b(${CONTROL_REASON_ORDER.join('|')})\\b`, 'g');
const ECCN_RE = /\b([0-9][A-E][0-9]{3})\b/;

export interface ControlConstraint {
  citation?: string | null;
  reason_for_control?: string | null;
}

export interface ControlInput {
  current_regime?: string | null;
  candidate_eccns?: string[] | null;
  candidate_usml?: string[] | null;
  binding_constraints?: ControlConstraint[] | null;
}

/** The reason-for-control codes named in `text`, de-duplicated and in display order. */
export const controlReasonCodes = (text?: string | null): string[] => {
  const found = new Set<string>();
  const matches = (text || '').match(REASON_CODE_RE) || [];
  matches.forEach((code) => found.add(code));
  return CONTROL_REASON_ORDER.filter((code) => found.has(code));
};

/** "National Security (NS), Encryption Items (EI) and Anti-Terrorism (AT)". */
export const reasonPhrase = (codes: string[]): string => {
  const names = codes.map((code) => `${CONTROL_REASON_NAMES[code]} (${code})`);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/** The five-character ECCN ("5A002") inside a citation like "5A002.a.3 (and Technical Note 2)". */
export const eccnBase = (text?: string | null): string | undefined => {
  const match = ECCN_RE.exec((text || '').toUpperCase());
  return match ? match[1] : undefined;
};

/** The control a classification is headlined by: the USML category for ITAR, the ECCN for
 *  EAR-listed, nothing for EAR99 (it has no entry of its own). */
export const primaryControlOf = (result: ControlInput): string | undefined => {
  const regime = result.current_regime;
  const constraints = result.binding_constraints || [];
  if (regime === 'ITAR') {
    return (result.candidate_usml || [])[0] || constraints[0]?.citation || undefined;
  }
  if (regime === 'EAR-listed') {
    return (result.candidate_eccns || [])[0] || constraints[0]?.citation || undefined;
  }
  return undefined;
};

/** The reasons for control of the primary ECCN, from the constraints that cite it (falling back to
 *  all of them when none does). Empty for anything but EAR-listed. */
export const controlReasonsOf = (result: ControlInput): string[] => {
  if (result.current_regime !== 'EAR-listed') return [];
  const constraints = result.binding_constraints || [];
  const base = eccnBase(primaryControlOf(result));
  const citing = constraints.filter((c) => base && eccnBase(c.citation) === base);
  const chosen = citing.length > 0 ? citing : constraints;
  const found = new Set<string>();
  chosen.forEach((c) => controlReasonCodes(c.reason_for_control).forEach((code) => found.add(code)));
  return CONTROL_REASON_ORDER.filter((code) => found.has(code));
};

/** One sentence on what the reasons mean for a licence, or '' when they are not known. Qualitative
 *  on purpose: which destinations need a licence depends on the Country Chart column behind each
 *  reason, so it never claims "most" or "all". */
export const licencePostureOf = (result: ControlInput): string => {
  const reasons = controlReasonsOf(result);
  if (reasons.length === 0) return '';
  if (reasons.length === 1 && reasons[0] === 'AT') {
    return (
      'This ECCN is controlled for Anti-Terrorism only, so a licence is generally needed only for ' +
      'embargoed or terrorism-sensitive destinations — the Country Chart settles it.'
    );
  }
  return (
    `This ECCN is controlled for ${reasonPhrase(reasons)}. Whether a licence is needed depends on ` +
    'the destination: each reason is read against the Commerce Country Chart, and a licence ' +
    'exception may apply.'
  );
};

/** The headline sentence: the control and what it is controlled for. '' for EAR99 / uncertain. */
export const controlSentenceOf = (result: ControlInput): string => {
  const primary = primaryControlOf(result);
  if (!primary) return '';
  if (result.current_regime === 'ITAR') {
    const label = /^\s*(usml|category)/i.test(primary) ? primary : `USML Category ${primary}`;
    return `${label} — controlled under the ITAR.`;
  }
  if (!eccnBase(primary)) return '';
  const reasons = controlReasonsOf(result);
  return `ECCN ${primary} — ${
    reasons.length > 0 ? `controlled for ${reasonPhrase(reasons)}` : 'on the Commerce Control List'
  }.`;
};
